import { Edit } from '@mui/icons-material'
import { Button, Card, CardContent, CircularProgress, Divider, Grid, Typography } from '@mui/material'
import { GreenText, LabelField } from 'components'
import * as controllers from 'controllers'
import { MichiganSOSBundleDto, MichiganTitleApplicationCoverLetterDto } from 'dtos'
import { useLoadingState } from 'hooks'
import { enqueueSnackbar } from 'notistack'
import React, { useEffect, useState } from 'react'
import { formatAddress, formatDateMoment, formatTimeMoment } from 'utils'
import MichiganSOSBundlesTitleApplicationCoverLetterEditDialog from './MichiganSOSBundlesTitleApplicationCoverLetterEditDialog'

interface MichiganSOSBundlesTitleApplicationCoverLetterListCardProps {
  michiganSOSBundle: MichiganSOSBundleDto
  dropOffDate?: string
  time?: string
  michiganSOSBranch?: string
  michiganSOSBranchAddress?: string
  description?: string
  contactPerson?: string
  mobilePhone?: string
  email?: string
}

export default function MichiganSOSBundlesTitleApplicationCoverLetterListCard({
  michiganSOSBundle,
  dropOffDate,
  time,
  michiganSOSBranch,
  michiganSOSBranchAddress,
  description,
  contactPerson,
  mobilePhone,
  email
}: MichiganSOSBundlesTitleApplicationCoverLetterListCardProps) {
  const [michiganSOSBundlesTitleApplicationCoverLetterEditDialogOpen, setMichiganSOSBundlesTitleApplicationCoverLetterEditDialogOpen] =
    React.useState<boolean>(false)
  const [miTitleApplicationCoverLetter, setMITitleApplicationCoverLetter] = useState<MichiganTitleApplicationCoverLetterDto>(
    new MichiganTitleApplicationCoverLetterDto()
  )

  const [loadingState, setLoadingState] = useLoadingState({
    isGettingMichiganSOSBundleTitleApplicationCoverLetterInformation: false,
    isUpdatingMichiganSOSBundleTitleApplicationCoverLetterInformation: false
  })

  useEffect(() => {
    if (michiganSOSBundle.michiganSOSBundleGuid) {
      setLoadingState('isGettingMichiganSOSBundleTitleApplicationCoverLetterInformation', true)
      controllers.michiganSoSBundles
        .getMichiganSOSBundlesTitleApplicationCoverLetterInformation(michiganSOSBundle.michiganSOSBundleGuid)
        .then(response => {
          setMITitleApplicationCoverLetter(response.value)
        })
        .finally(() => {
          setLoadingState('isGettingMichiganSOSBundleTitleApplicationCoverLetterInformation', false)
        })
    }
  }, [michiganSOSBundle.michiganSOSBundleGuid])

  return (
    <>
      <MichiganSOSBundlesTitleApplicationCoverLetterEditDialog
        initialValues={miTitleApplicationCoverLetter}
        isLoading={loadingState.isUpdatingMichiganSOSBundleTitleApplicationCoverLetterInformation}
        onClose={() => setMichiganSOSBundlesTitleApplicationCoverLetterEditDialogOpen(false)}
        onSave={values => {
          setLoadingState('isUpdatingMichiganSOSBundleTitleApplicationCoverLetterInformation', true)
          controllers.michiganSoSBundles
            .updateMichiganSOSBundlesTitleApplicationCoverLetterInformation(michiganSOSBundle.michiganSOSBundleGuid!, values)
            .then(response => {
              setMITitleApplicationCoverLetter(response.value)
              setMichiganSOSBundlesTitleApplicationCoverLetterEditDialogOpen(false)
              enqueueSnackbar('MI Title Application Cover Letter Saved Successfully!', { variant: 'success' })
            })
            .finally(() => {
              setLoadingState('isUpdatingMichiganSOSBundleTitleApplicationCoverLetterInformation', false)
            })
        }}
        open={michiganSOSBundlesTitleApplicationCoverLetterEditDialogOpen}
      />

      <Grid item xs={12} mb={1}>
        <Card>
          <CardContent>
            <Grid container spacing={1} alignItems='flex-start'>
              <Grid item xs={12} container alignItems='center' justifyContent='space-between'>
                <Grid item xs={12} sm={true}>
                  <Typography fontWeight='bold' variant='h5'>
                    MI Title Application Cover Letter
                  </Typography>
                </Grid>

                <Grid item xs={12} sm='auto'>
                  <Button
                    endIcon={<Edit />}
                    onClick={() => {
                      setMichiganSOSBundlesTitleApplicationCoverLetterEditDialogOpen(true)
                    }}
                    variant='text'
                    disabled={michiganSOSBundle.isLocked}
                  >
                    EDIT
                  </Button>
                </Grid>
              </Grid>

              <Grid item xs={12}>
                <Divider />
              </Grid>

              {loadingState.isGettingMichiganSOSBundleTitleApplicationCoverLetterInformation && (
                <Grid item container alignItems='center' justifyContent='center' sx={{ mt: 1 }}>
                  <Grid item>
                    <CircularProgress color='primary' size={50} />
                  </Grid>
                </Grid>
              )}

              {!loadingState.isGettingMichiganSOSBundleTitleApplicationCoverLetterInformation && (
                <>
                  <Grid item xs={12}>
                    <LabelField label='Drop Off Date' value={formatDateMoment(miTitleApplicationCoverLetter.dropOffDate)} />
                  </Grid>

                  <Grid item xs={12}>
                    <LabelField label='Time' value={formatTimeMoment(miTitleApplicationCoverLetter.dropOffTime)} />
                  </Grid>

                  <Grid item xs={12}>
                    <LabelField label='Michigan SoS Branch' value={michiganSOSBranch ?? miTitleApplicationCoverLetter.michiganSOSBranchCompany?.name} />
                  </Grid>

                  <Grid item xs={12}>
                    <LabelField
                      label='Michigan SoS Branch Address'
                      value={
                        miTitleApplicationCoverLetter.michiganSOSBranchCompanyAddress
                          ? formatAddress(miTitleApplicationCoverLetter.michiganSOSBranchCompanyAddress)
                          : undefined
                      }
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <LabelField label='Description' value={miTitleApplicationCoverLetter.description} />
                  </Grid>

                  <Grid item xs={12}>
                    <LabelField
                      label='Contact Person'
                      value={
                        miTitleApplicationCoverLetter.contactPerson
                          ? `${miTitleApplicationCoverLetter.contactPerson.firstName} ${miTitleApplicationCoverLetter.contactPerson.lastName}`
                          : undefined
                      }
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <LabelField label='Mobile Phone' value={miTitleApplicationCoverLetter.contactPerson?.mobilePhone ?? ''} />
                  </Grid>

                  <Grid item xs={12}>
                    <LabelField label='Email' value={miTitleApplicationCoverLetter.contactPerson?.email ?? ''} />
                  </Grid>
                </>
              )}
            </Grid>

            <Grid item xs={12} mt={2}>
              <GreenText variant='outlined'>
                {`**Edit Button:**

Clicking this button opens the MI Title Application Cover Letter Dialog.

**Drop Off Date:**

Displays the Drop Off Date entered in the MI Title Application Cover Letter Dialog.

If no value is entered, a “-” will be displayed.

**Time:**

Displays the time entered in the MI Title Application Cover Letter Dialog.

If no value is entered, a “-” will be displayed.

**MI SoS Branch:**

Displays the selected Michigan SoS Branch from the MI Title Application Cover Letter Dialog.

If no value is entered, a “-” will be displayed.

**Description:**

Displays the description entered in the MI Title Application Cover Letter Dialog.

If no value is entered, a “-” will be displayed.

**Contact Person:**

Displays the selected Contact Person from the MI Title Application Cover Letter Dialog.

If no value is entered, a “-” will be displayed.

**Mobile Phone:**

Displays the mobile phone number, auto-populated based on the selected Contact Person in the MI Title Application Cover Letter Dialog.

If no value is entered, a “-” will be displayed.

**Email:**

Displays the email address, auto-populated based on the selected Contact Person in the MI Title Application Cover Letter Dialog.

If no value is entered, a “-” will be displayed.`}
              </GreenText>
            </Grid>
          </CardContent>
        </Card>
      </Grid>
    </>
  )
}
